/**
 * An `Error` or an object shaped like one. Covers values that fail
 * `instanceof Error` but still carry the useful fields: cross-realm errors
 * (iframes, workers), `DOMException` in older engines, and SDK rejections
 * that copy `{ name, message, stack }` onto a plain object.
 */
export interface ErrorLike {
  cause?: unknown;
  message: string;
  name?: string;
  stack?: string;
}

/**
 * True for `Error` instances and for objects with a string `message` plus a
 * string `name` or `stack`. Requiring the second field keeps plain data
 * objects like `{ code, message }` (API error bodies) out, so they still
 * serialize with all of their fields.
 *
 * Reads properties directly rather than relying on enumerability — an
 * error's `message` and `stack` are non-enumerable, which is why
 * `JSON.stringify` turns them into `{}`.
 */
export function isErrorLike(value: unknown): value is ErrorLike {
  if (value instanceof Error) return true;
  if (typeof value !== 'object' || value === null) return false;
  const { message, name, stack } = value as Record<string, unknown>;
  return (
    typeof message === 'string' &&
    (typeof name === 'string' || typeof stack === 'string')
  );
}

const STANDARD_ERROR_FIELDS = new Set(['cause', 'message', 'name', 'stack']);

/**
 * Own enumerable fields of an error-shaped object other than the standard
 * error fields (e.g. `code` on an SDK rejection), so serializers that
 * pick `{ message, name, stack }` don't drop them. Returns `undefined` for
 * `Error` instances, whose serialized shape is unchanged, and when there
 * is nothing extra.
 */
export function errorLikeDetails(
  value: ErrorLike,
): Record<string, unknown> | undefined {
  if (value instanceof Error) return undefined;
  const details = Object.fromEntries(
    Object.entries(value).filter(([key]) => !STANDARD_ERROR_FIELDS.has(key)),
  );
  return Object.keys(details).length > 0 ? details : undefined;
}
